import { buildLogObject } from '@/lib/auditLogsHelper';
import { getFromLS, setToLS } from '@/lib/commonHelpers';
import { RealtimeDbCreateManyValue } from '@/providers/dataProvider/firestoreDataProvider';
import * as Sentry from '@sentry/react';
import { pickBy } from 'lodash';

// Views that mean "the student's test is submitted" — the Submitted chip filters
// by this list too, so both surfaces stay in sync.
export const SUBMITTED_VIEWS = ['submittedTest', 'viewFeedback'] as const;

export const isTestSubmitted = (currentView: string) => {
  return (SUBMITTED_VIEWS as readonly string[]).includes(currentView);
};

/**
 * Effective student accommodations, resolved per key:
 *   monitor (user_info.accommodations) ?? userList (custom_fields) || delivery (settings)
 * userList and delivery can only ENABLE; an explicit monitor value (incl. `false`)
 * always wins. Keys no source enables are omitted, so they keep re-resolving on
 * the next read — `user_info.accommodations` stays monitor-authored only.
 *
 * NOTE: the `student_accommodations` feature flag is intentionally NOT consulted
 * here — with the flag off, accommodations from user custom fields still apply.
 * The flag is already released on production, so this only affects orgs where it
 * is explicitly turned off. (PR #6691 parity.)
 */
export const resolveAccommodations = (
  userCustomFieldsAccommodations?: any,
  experienceSettings?: any,
  monitorAccommodations?: any
): Record<string, any> => {
  // Delivery settings configure only these three tools; keep the enabled ones.
  const delivery = pickBy({
    spellcheck: experienceSettings?.spellcheck_enabled,
    calc_enabled: experienceSettings?.calc_enabled,
    graphing_calc_enabled: experienceSettings?.graphing_calc_enabled,
  });
  // userList enables via `true` or a non-empty string (e.g. additional_time: '25');
  // a translate `false` is kept so it beats the org flag default.
  const userList = pickBy(
    userCustomFieldsAccommodations ?? {},
    (value, key) =>
      value === true ||
      (typeof value === 'string' && !!value) ||
      (key === 'allow_student_to_translate' && value === false)
  );
  // Monitor overrides everything it explicitly set.
  const monitor = pickBy(monitorAccommodations ?? {}, (value) => value != null);

  return { ...delivery, ...userList, ...monitor };
};

/**
 * Decide whether an offline user response should be pushed to Firestore.
 *
 * A response is pushed only when its `backup_generated_at` is newer than
 * `offline_urs_last_saved_at`. Every write stamps a fresh monotonic
 * `backup_generated_at` in useRealtimeUserResponses, so this single
 * comparison is enough — no separate synced-id bookkeeping required.
 */
export const shouldSyncOfflineUserResponse = (
  response: { backup_generated_at?: number },
  lastSavedAt: number | string | null | undefined
): boolean => {
  if (!response.backup_generated_at) return true;
  // Legacy rows persisted lastSavedAt as an ISO string; normalize to epoch ms.
  const lastSavedMs =
    typeof lastSavedAt === 'number' ? lastSavedAt : Date.parse(lastSavedAt ?? '') || 0;
  return response.backup_generated_at > lastSavedMs;
};

export type StudentPauseState = string | null | undefined;

// Pass no status (or null) to check for any "effectively paused" state: all
// three pause statuses freeze the student's timer until the handshake
// completes. Callsites that disable actions or render paused UI should use
// the no-arg form; callsites that distinguish states (e.g. offset
// reconciliation branches) must pass an explicit status.
export const hasPauseStatus = (
  pauseState: StudentPauseState,
  status: StudentPauseState = null
): boolean => {
  if (!status) {
    return (
      pauseState === 'paused' || pauseState === 'pause_pending' || pauseState === 'unpause_pending'
    );
  }
  return pauseState === status;
};

export function getOrCreateSessionId(cId?: string) {
  try {
    let storedId: string | null =
      sessionStorage.getItem('currentSessionId') ?? getFromLS('currentSessionId');

    if (!storedId && cId) {
      storedId = cId;
      try {
        sessionStorage.setItem('currentSessionId', storedId);
      } catch (sessionError) {
        setToLS('currentSessionId', storedId);
        Sentry.captureException(sessionError);
      }
    }

    return storedId;
  } catch (error) {
    console.error('Error accessing storage', error);
    Sentry.captureException(error);
    return '';
  }
}

// Move the constant into a function
// export const getOnlinePriorityKeys = () => [
//   'extra_time',
//   'extra_time_offset',
//   'security',
//   'request_resume',
//   'needs_attention_at',
//   'student_unread_count',
// ];

export const updateCurrentView = (sessionInfo: any, userInfoData: any, options: any) => {
  const {
    appType,
    experienceSettings,
    switchFromStartToResumeView,
    sessionIdFromStorage,
    logOptions,
  } = options;

  return;
};

// J25-M37 (dev decision 2026-07-28): v3 semantics restored — `persisted || url`,
// and init writes the result back, so the doc flag is sticky-true and a phone
// refresh that lost the query param stays in upload mode. Reverts the MQ-02
// URL-only design (576d1c21c0); the known downside (sticky flag can put a later
// iPad open into upload mode) is accepted; a per-tab fix is a separate ticket.
export const isMobileUploadMode = (userInfoData: any) =>
  userInfoData?.mobile_upload_mode || location.search.includes('mobileUploadMode');

/**
 * NEW-1 (U1) fail-close. A window OPENED via the QR "upload handwritten answers"
 * link (its URL carries `mobileUploadMode`) is ALWAYS an upload-only session
 * regardless of device — this re-engages the strip-only upload surface on a
 * desktop / 2nd window that `checkMobileLikeDevice()` would otherwise route to
 * the full answerable take (the U1 wipe path). The PERSISTED user_info flag
 * alone still needs a mobile-like device (an iPad resume where the flag was
 * written by the phone), so a normal desktop take is never forced into upload
 * mode. Pure so the fail-close is drift-pinnable. J25-M37 (2026-07-28): v3
 * semantics restored, reverting the MQ-02 URL-only form (576d1c21c0).
 */
export const resolveIsMobileUploadSession = (
  urlHasUploadMode: boolean,
  persistedUploadMode: boolean,
  isMobileLikeDevice: boolean
): boolean => urlHasUploadMode || (persistedUploadMode && isMobileLikeDevice);

// `browser` is persisted as "<name>-<version>" (e.g. "chrome-120"); the
// fallback tier of `hasDeviceInfoChanged` compares just the name so a
// browser auto-update (chrome-119 → chrome-120) does not falsely register
// as a device swap. Returns `''` for null / undefined / non-string input
// (treated as "no browser" — equality of two empty strings is still
// equality, which is the right call for shells that don't expose a
// browser field).
const normalizeBrowserName = (browser: unknown): string => {
  if (typeof browser !== 'string') return '';
  return browser.split('-')[0] ?? '';
};

/**
 * Decide whether `currentDeviceInfo` represents a different physical device
 * than `existingDeviceInfo`. Used to gate the `usedDevices` append + the
 * device-change audit log in InitRealtimeExperience, the response-cache
 * rehydrate in useMultiSourceUserResponseHydration, and the sync-recovery
 * branching in LocalStorageSyncView.
 *
 * Returns `true` when the devices differ (or when we cannot tell — fail
 * safe by triggering the change flow); `false` only when we have positive
 * evidence the device is the same.
 *
 * Two-tier comparison:
 *
 *   1. Authoritative — `serialNo`. When both sides have a non-empty
 *      `serialNo` string we trust it: equal → same device, differ →
 *      different device. We do NOT fall through to the legacy tier in
 *      this case, so a Mac with the same `serialNo` after an OS minor
 *      update or app update correctly stays "same".
 *
 *   2. Fallback — when either side lacks `serialNo` (web shells, SEB,
 *      older native builds that pre-date the field). Compare
 *      `app_type`, `os`, `osVersion`, `browser` (name-only — the version
 *      suffix is stripped so chrome-119 ≡ chrome-120), and `appVersion`.
 *      Any mismatch → different device.
 *
 * Empty-string `serialNo` on either side is treated as "not present" and
 * falls through to the fallback tier. This defends against shell builds
 * that initialise the field before the native bridge populates it.
 *
 * Null / undefined / non-object inputs return `true` (fail safe — trigger
 * the change flow rather than silently treat unknown state as "same").
 */
export const hasDeviceInfoChanged = (existingDeviceInfo: any, currentDeviceInfo: any): boolean => {
  if (!existingDeviceInfo) return true;
  if (
    typeof existingDeviceInfo !== 'object' ||
    typeof currentDeviceInfo !== 'object' ||
    !currentDeviceInfo
  ) {
    return true;
  }

  const existingSerial =
    typeof existingDeviceInfo.serialNo === 'string' ? existingDeviceInfo.serialNo : '';
  const currentSerial =
    typeof currentDeviceInfo.serialNo === 'string' ? currentDeviceInfo.serialNo : '';

  // Tier 1 — authoritative. Both sides must have a non-empty serialNo
  // for the comparison to be trustworthy.
  if (existingSerial !== '' && currentSerial !== '') {
    return existingSerial !== currentSerial;
  }

  // Tier 2 — fallback. Legacy comparison broadened to include
  // app_type + osVersion alongside the original os / browser / appVersion
  // check so a swap between, say, two Macs of the same OS family still
  // surfaces when serialNo is absent.
  if (existingDeviceInfo.app_type !== currentDeviceInfo.app_type) return true;
  if (existingDeviceInfo.os !== currentDeviceInfo.os) return true;
  if (existingDeviceInfo.osVersion !== currentDeviceInfo.osVersion) return true;
  if (existingDeviceInfo.appVersion !== currentDeviceInfo.appVersion) return true;
  if (
    normalizeBrowserName(existingDeviceInfo.browser) !==
    normalizeBrowserName(currentDeviceInfo.browser)
  ) {
    return true;
  }

  return false;
};

export const buildRealtimeLogPayload = (log: any, options: any): RealtimeDbCreateManyValue => {
  const {
    currentUser,
    experience,
    userIp,
    isOnline,
    collection = 'experiences',
    multipleAttemptsEnabled = false,
    attemptNo,
  } = options;
  const experienceId = experience.id;
  const orgId = experience.org_id;
  const resourcePath =
    collection === 'experiences' ? `experiences/${experienceId}/` : `orgs/${orgId}/`;
  const logObj = buildLogObject(currentUser, log, {
    orgId: experience.org_id,
    experienceId: experience.id,
    internetStatus: log.internet_status ?? (isOnline ? 'online' : 'offline'),
    userIp,
  });
  // Tag the attempt (never write undefined — Firestore rejects it). Gated on the
  // org's multiple_attempts flag: the China provider writes this object straight into
  // a Supabase logs table, which rejects every insert when the column is absent.
  if (multipleAttemptsEnabled && attemptNo != null) {
    logObj.attempt_no = Number(attemptNo) || 1;
  }
  return {
    resource: 'logs',
    resourcePath: resourcePath,
    uuids: [logObj.log_id],
    ids: [logObj.log_id],
    data: logObj,
  };
};
